﻿import {AdminPaymentViewModel} from './adminPayment.viewmodel';
import * as Repositories from '../../repositories/repositories'
import * as Model from '../../models/models';
import * as _ from 'lodash';

export class AdminPaymentGateway {

    private numberOfAttempts: number = 0;
    private paymentMethodsHaveChanged: boolean = false;
    private deferred;
    private maximumAttempts = 40;
    private delayDuration: number = 500;

    static $inject = ['$q', '$timeout', 'policyholderRepository', 'paymentRepository', 'billingStatementRepository'];

    constructor(private $q: ng.IQService,
        private $timeout: ng.ITimeoutService,
        private policyholderRepository: Repositories.PolicyholderRepository,
        private paymentRepository: Repositories.PaymentRepository,
        private billingStatementRepository: Repositories.BillingStatementRepository    ) {
    }


    public loadAdminPaymentViewModel = (policyholderid:string): ng.IPromise<AdminPaymentViewModel> => {

        let adminPaymentViewModel = new AdminPaymentViewModel;

        return this.setPolicyholder(adminPaymentViewModel, policyholderid)
            .then((viewModel: AdminPaymentViewModel): ng.IPromise<AdminPaymentViewModel> => {
                return this.setBillingStatement(viewModel);
            })
            .then((viewModel: AdminPaymentViewModel): ng.IPromise<AdminPaymentViewModel> => {
                return this.setPaymentMethods(viewModel);
            })
            .then((viewModel: AdminPaymentViewModel): ng.IPromise<AdminPaymentViewModel> => {
                return this.setInitialPaymentAmount(viewModel);
            })
            .then((viewModel: AdminPaymentViewModel): ng.IPromise<AdminPaymentViewModel> => {
                return this.setPolicyholderPaymentNotification(viewModel, policyholderid);
            });
    };

    private setPolicyholderPaymentNotification = (viewModel: AdminPaymentViewModel, policyholderid:string): ng.IPromise<AdminPaymentViewModel> => {
        return this.policyholderRepository.getPaymentNotification(policyholderid)
            .then((policyholderPaymentNotification: Model.IPolicyholderPaymentNotification) => {
                if (policyholderPaymentNotification) {
                    viewModel.policyholderPaymentNotification = policyholderPaymentNotification;                
                }
                else {
                    let policyholderPaymentNotification = new Model.PolicyholderPaymentNotification;
                    policyholderPaymentNotification.policyholderId = viewModel.policyholder.id;
                    viewModel.policyholderPaymentNotification = policyholderPaymentNotification; 
                }
                return viewModel;
            });
    };

    private setPolicyholder = (viewModel: AdminPaymentViewModel, policyholderid:string): ng.IPromise<AdminPaymentViewModel> => {
        return this.policyholderRepository.getById(policyholderid)
            .then((policyholder: Model.IPolicyholder) => {
                viewModel.policyholder = policyholder;
                return viewModel;
            });
    };

    public setPaymentMethods = (viewModel: AdminPaymentViewModel): ng.IPromise<AdminPaymentViewModel> => {

        return this.$q.all([
            this.getCardPaymentMethods(viewModel).then((cardPaymentMethods: Array<Model.ICardPaymentMethod>) => viewModel.cardPaymentMethods = cardPaymentMethods),
            this.getEcheckPaymentMethods(viewModel).then((eCheckPaymentMethods: Array<Model.IEcheckPaymentMethod>) => viewModel.echeckPaymentMethods = eCheckPaymentMethods)
        ]).then(() => {
            this.setAutoPayToken(viewModel);
            return viewModel;
        });
    };

    public setInitialPaymentAmount = (viewModel: AdminPaymentViewModel): ng.IPromise<AdminPaymentViewModel> => {
        viewModel.paymentAmount = viewModel.amountDueValue();
        return this.$q.when(viewModel);

    };
    public makePayment = (viewModel: AdminPaymentViewModel): ng.IPromise<AdminPaymentViewModel> => {

        let paymentRequest = new Model.PaymentRequest();
        paymentRequest.amount = viewModel.paymentAmount;
        paymentRequest.paymentTokenType = this.getSelectedMethodType(viewModel);
        paymentRequest.tokenId = viewModel.selectedPaymentTokenId;
        paymentRequest.policyHolderId = viewModel.policyholder.id;
        paymentRequest.requestOriginType = "ADMINISTRATOR";

        return this.paymentRepository.makePayment(paymentRequest)
            .then((paymentResponse: Model.IPaymentResponse) => {
                viewModel.paymentResponse = paymentResponse;
                return viewModel;
            });

    };
    
    public savePolicyholderPaymentNotification(PolicyholderPaymentNotification: Model.IPolicyholderPaymentNotification): ng.IPromise<Model.IPolicyholderPaymentNotification> {
        return this.policyholderRepository.savePaymentNotification(PolicyholderPaymentNotification)
        .then((paymentResponse: Model.IPolicyholderPaymentNotification) => {
            PolicyholderPaymentNotification = paymentResponse;
            return PolicyholderPaymentNotification;
        });
    }

    private setAutoPayToken = (viewModel: AdminPaymentViewModel): ng.IPromise<AdminPaymentViewModel> => {
        const autoPayCardToken = _.find(viewModel.cardPaymentMethods, (cardPaymentMethod) => { return cardPaymentMethod.autopay });
        const autoPayEcheckToken = _.find(viewModel.echeckPaymentMethods, (echeckPaymentMethod) => { return echeckPaymentMethod.autopay });

        if (typeof (autoPayCardToken) != "undefined") {
            viewModel.selectedPaymentTokenId = autoPayCardToken.tokenId;
            return;
        }

        if (typeof (autoPayEcheckToken) != "undefined") {
            viewModel.selectedPaymentTokenId = autoPayEcheckToken.tokenId;
            return;
        }

    };

    private getCardPaymentMethods = (viewModel: AdminPaymentViewModel): ng.IPromise<Array<Model.ICardPaymentMethod>> => {
        return this.paymentRepository.getCardPaymentMethods(viewModel.policyholder.id)
            .then((cardPaymentMethods: Array<Model.ICardPaymentMethod>) => {
                return cardPaymentMethods;
            });
    };

    private getEcheckPaymentMethods = (viewModel: AdminPaymentViewModel): ng.IPromise<Array<Model.IEcheckPaymentMethod>> => {
        return this.paymentRepository.getEcheckPaymentMethods(viewModel.policyholder.id)
            .then((echeckPaymentMethods: Array<Model.IEcheckPaymentMethod>) => {
                return echeckPaymentMethods;
            });
    };

    private setBillingStatement = (viewModel: AdminPaymentViewModel): ng.IPromise<AdminPaymentViewModel> => {

        return this.billingStatementRepository.getById(viewModel.policyholder.id)
            .then((billingStatement: Model.IBillingStatement) => {
                viewModel.billingStatement = billingStatement;
                return viewModel;
            });
    };

    private updatePaymentMethods = (viewModel: AdminPaymentViewModel): ng.IPromise<any> => {

        if (this.numberOfAttempts < this.maximumAttempts && this.paymentMethodsHaveChanged === false) {

            let newCardPaymentMethods: Array<Model.ICardPaymentMethod>;
            let newEcheckPaymentMethods: Array<Model.IEcheckPaymentMethod>;

            let callChain1 = this.getCardPaymentMethods(viewModel)
                .then((cardPaymentMethods: Array<Model.ICardPaymentMethod>) => newCardPaymentMethods =
                    cardPaymentMethods);
            let callChain2 = this.getEcheckPaymentMethods(viewModel)
                .then((eCheckPaymentMethods: Array<Model.IEcheckPaymentMethod>) => newEcheckPaymentMethods =
                    eCheckPaymentMethods);

            this.$q.all([callChain1, callChain2])
                .then(() => {

                    this.numberOfAttempts = this.numberOfAttempts + 1;

                    let cardPaymentMethodsHaveChanged = !_.isEqual(newCardPaymentMethods, viewModel.cardPaymentMethods);
                    let echeckPaymentMethodsHaveChanged = !_.isEqual(newEcheckPaymentMethods, viewModel.echeckPaymentMethods);

                    if (cardPaymentMethodsHaveChanged || echeckPaymentMethodsHaveChanged) {
                        this.paymentMethodsHaveChanged = true;
                        viewModel.cardPaymentMethods = newCardPaymentMethods;
                        viewModel.echeckPaymentMethods = newEcheckPaymentMethods;
                    }

                    this.$timeout(() => {
                        this.updatePaymentMethods(viewModel);

                    }, this.delayDuration);
                });

        }
        if (this.numberOfAttempts >= this.maximumAttempts && this.paymentMethodsHaveChanged === false) {
            return this.deferred.reject("Maximum attempts reached");
        }

        if (this.paymentMethodsHaveChanged) {
            return this.deferred.resolve(true);
        }

    };

    private getSelectedMethodType = (viewModel: AdminPaymentViewModel): string => {

        let selectedMethodType = "";

        const isCard = _.find(viewModel.cardPaymentMethods, (o: Model.ICardPaymentMethod) => (o.tokenId === viewModel.selectedPaymentTokenId));
        const isECheck = _.find(viewModel.echeckPaymentMethods, (o: Model.IEcheckPaymentMethod) => (o.tokenId === viewModel.selectedPaymentTokenId));
        if (typeof (isCard) != "undefined") { selectedMethodType = "CARD"; }
        if (typeof (isECheck) != "undefined") { selectedMethodType = "ECHECK"; }

        return selectedMethodType;

    }


}